import { useTheme } from '@mui/material';

import { TFunction } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ContentTemplate,
  HeaderWithPill,
  HtmlTrans,
  Icon,
  MUIDataTable,
  MUIDataTableColumnDef,
  TableActionTypes,
  TableState,
  TABLE_INITIAL_STATE,
  Typography,
  useDataTable,
  useSnackbarHandler,
} from 'web-library';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import {
  RequestType,
  useGetEnabledRequestTypes,
} from '../../../../api-refactored/candidate/useGetEnabledRequestTypes';
import { usePutDisableRequestTypes } from '../../../../api-refactored/candidate/usePutDisableRequestTypes';
import DeleteButton from '../../../../components/buttons/DeleteButton';
import {
  GET_DROPDOWN_REQUEST_TYPES_QUERY_KEY,
  GET_ENABLED_REQUEST_TYPES_QUERY_KEY,
} from '../../../../constants/queryKeys';
import { stringToLongDateAndShortTime } from '../../../../utils/DateUtils';
import AddRequestTypeModal from './AddRequestTypeModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { useStyles } from './style';

const getTableColumns = (
  t: TFunction,
  classes: Record<string,string>,
  tableState: TableState,
  onDeleteDialogOpen: () => void,
  setSelectedRequestType: React.Dispatch<React.SetStateAction<RequestType>>,
  isEnableDelete: boolean
): MUIDataTableColumnDef[] => [
  {
    name: 'requestType',
    label: t('admin->>AccessArrangementConfiguration.AccessArrangementType'),
    options: {
      customBodyRenderLite: (dataIndex: number) => tableState.data[dataIndex].accessArrangementType,
    },
  },
  {
    name: 'modeOfAssessment.description',
    label: t('admin->>AccessArrangementConfiguration.MOA'),
    options: {
      customBodyRenderLite: (dataIndex: number) => tableState.data[dataIndex].modeOfAssessment,
    },
  },
  {
    name: 'action',
    label: t('admin->>AccessArrangementConfiguration.Actions'),
    options: {
      sort: false,
      customBodyRenderLite: (dataIndex: number) =>
        DeleteButton({
          deleteHandler: () => {
            setSelectedRequestType(tableState.data[dataIndex]);
            onDeleteDialogOpen();
          },
          label: t('component->>IconLabel.Remove'),
          className: classes.deleteButton,
        }),
      display: `${isEnableDelete}`,
    },
  },
];

const getRequestTypeDisplayString = (requestType: RequestType) =>
  `${requestType.accessArrangementType} (${requestType.modeOfAssessment})`;

interface RequestTypeConfigurationProps {
  isEnableCreate: boolean;
  isEnableDelete: boolean;
}

const RequestTypeConfiguration = ({
  isEnableCreate,
  isEnableDelete,
}: RequestTypeConfigurationProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [addRequestModalState, setAddRequestModalState] = useState<boolean>(false);

  const onAddRequestTypeClicked = () => {
    // to ensure the dropdown list is the latest data
    queryClient.invalidateQueries(GET_DROPDOWN_REQUEST_TYPES_QUERY_KEY);
    setAddRequestModalState(true);
  };

  // ------------------------------- table data logic --------------------------------------
  const { state: tableState, dispatch: tableDispatch, tOptions: options } = useDataTable({
    ...TABLE_INITIAL_STATE,
    sortColumn: 'requestType',
    size: 10,
  });
  const { data, isLoading, refetch } = useGetEnabledRequestTypes({
    params: {
      page: tableState.page,
      pageSize: tableState.size,
      order: tableState.order,
      sort: tableState.sortColumn,
    },
    onSuccess: (res) => {
      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: res.requestTypeModels.content,
          count: res.requestTypeModels.totalElements,
        },
      } as TableActionTypes);
    },
  });

  useEffect(() => {
    refetch();
  }, [tableState.page, tableState.size, tableState.order, tableState.sortColumn, refetch]);

  // ------------------------------- table data logic --------------------------------------

  // ------------------------------- snackbar logic ----------------------------------------
  const { snackbarHandler } = useSnackbarHandler();

  // ------------------------------- snackbar logic ----------------------------------------

  // ------------------------------- Delete logic ----------------------------------------
  const [deleteModalState, setDeleteModalState] = useState<boolean>(false);
  const [selectedRequestType, setSelectedRequestType] = useState<RequestType>({
    accessArrangementType: '',
    modeOfAssessment: '',
    uuidOfType: '',
  });
  const deleteRequestType = usePutDisableRequestTypes({
    onSuccess: () => {
      queryClient.invalidateQueries(GET_ENABLED_REQUEST_TYPES_QUERY_KEY);
      onDeleteDialogClose();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'success',
        targetData: t('admin->>AccessArrangementConfiguration.RemoveSuccessfullyMsg', {
          name: getRequestTypeDisplayString(selectedRequestType),
        }),
      });
    },
    onError: () => {
      onDeleteDialogClose();
      snackbarHandler?.setGlobalSnackbar({
        targetSeverity: 'error',
        targetData: t('admin->>AccessArrangementConfiguration.UnableToRemoveMsg', {
          name: getRequestTypeDisplayString(selectedRequestType),
        }),
        targetAnchorOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
      });
    },
  });
  const onDeleteDialogOpen = () => {
    setDeleteModalState(true);
  };
  const onDeleteDialogClose = () => {
    setDeleteModalState(false);
    deleteRequestType.reset();
  };
  const onDeleteDialogConfirm = () => {
    deleteRequestType.mutate(selectedRequestType.uuidOfType);
  };
  // ------------------------------- Delete logic ----------------------------------------

  return (
    <ContentTemplate type="full">
      <Box className={classes.headerContainer}>
        <HeaderWithPill
          title={t('admin->>AccessArrangementConfiguration.SelectedRequestTypes')}
          pillValue={tableState.count.toString()}
        />
        {isEnableCreate && (
          <Button
            id="button-addRequestType"
            variant="text"
            color="primary"
            startIcon={<Icon name={IconName.add} size="S" color={theme.color.icon.iconSelected} />}
            onClick={onAddRequestTypeClicked}
            className={classes.addButton}
          >
            {t('admin->>AccessArrangementConfiguration.AddRequestType')}
          </Button>
        )}
      </Box>
      <Typography className={classes.instruction}>
        {t('admin->>AccessArrangementConfiguration.RequestTypeInstruction')}
      </Typography>
      <MUIDataTable
        isLoading={isLoading}
        columns={getTableColumns(
          t,
          classes,
          tableState,
          onDeleteDialogOpen,
          setSelectedRequestType,
          isEnableDelete
        )}
        data={tableState.data}
        dispatch={tableDispatch}
        state={tableState}
        options={{
          ...options,
          sort: true,
          sortOrder: {
            name: tableState.sortColumn,
            direction: tableState.order,
          },
          rowsPerPage: tableState.size,
          pagination: true,
        }}
      />
      {data?.dateTime != null && (
        <div className={classes.updatedBy}>
          <IconWithLabel
            iconName={IconName.info}
            iconSize="M"
            iconColor={theme.color.icon.iconSelected}
            label={t('admin->>AccessArrangementConfiguration.DataUpdatedByMessage', {
              updatedBy: data.name,
              updatedAt: stringToLongDateAndShortTime(data.dateTime),
            })}
            typographyProps={{
              variant: 'subtitle2',
              color: 'textSecondary',
            }}
          />
        </div>
      )}

      <AddRequestTypeModal
        modalState={addRequestModalState}
        setModalState={setAddRequestModalState}
      />

      <DeleteConfirmationModal
        apiStatus={deleteRequestType.status}
        dialogContentMsg={
          <HtmlTrans
            i18nKey="admin->>AccessArrangementConfiguration.RemoveConfirmationMsg"
            values={{
              name: getRequestTypeDisplayString(selectedRequestType),
            }}
          />
        }
        dialogTitle={t('admin->>AccessArrangementConfiguration.RemoveRequestType')}
        modalState={deleteModalState}
        onDialogClose={onDeleteDialogClose}
        onDialogConfirm={onDeleteDialogConfirm}
      />
    </ContentTemplate>
  );
};

export default RequestTypeConfiguration;
